import { useState } from 'react';
import { callFn } from '../../lib/api';
import type { Item } from '../../lib/types';
import { tagNames } from '../../lib/types';
import { useApp } from '../../app/AppContext';
import { DataTable, type Column } from '../../components/DataTable';

interface FosterRow { foster_name: string | null; foster_email: string; reservation_count: number; total_units: number; est_value: string; }
interface SummaryRow { category: string; condition: string; tag: string; line_items: number; total_units: number; est_value: string; }

export function Reports() {
  const { toast } = useApp();
  const [tab, setTab] = useState<'foster' | 'summary' | 'items'>('foster');

  return (
    <>
      <h1 className="page-title">Reports</h1>
      <p className="page-sub">What fosters have taken, the donations summary for year-end tax estimates, and a full item list.</p>
      <div className="toolbar">
        <button className={`btn ${tab === 'foster' ? '' : 'secondary'} small`} onClick={() => setTab('foster')}>By foster</button>
        <button className={`btn ${tab === 'summary' ? '' : 'secondary'} small`} onClick={() => setTab('summary')}>Donations summary</button>
        <button className={`btn ${tab === 'items' ? '' : 'secondary'} small`} onClick={() => setTab('items')}>Items</button>
      </div>
      {tab === 'foster' ? <ByFoster toast={toast} /> : tab === 'summary' ? <Summary toast={toast} /> : <ItemsReport toast={toast} />}
    </>
  );
}

function ByFoster({ toast }: { toast: (t: string, k?: 'ok' | 'err' | 'info') => void }) {
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState<FosterRow[]>([]);
  const [loaded, setLoaded] = useState(false);

  async function run() {
    try {
      const res = await callFn<{ rows: FosterRow[] }>('report_by_foster', period === 'month' ? { period, month } : { period });
      setRows(res.rows); setLoaded(true);
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  return (
    <>
      <div className="toolbar">
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="month">By month</option><option value="ytd">Year to date</option>
        </select>
        {period === 'month' && <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />}
        <button className="btn small" onClick={run}>Run</button>
      </div>
      {loaded && (
        <DataTable
          columns={[
            { key: 'foster', label: 'Foster', get: (r: FosterRow) => r.foster_name || r.foster_email },
            { key: 'res', label: 'Reservations', get: (r: FosterRow) => r.reservation_count, numeric: true, align: 'right' },
            { key: 'units', label: 'Units', get: (r: FosterRow) => r.total_units, numeric: true, align: 'right' },
            { key: 'value', label: 'Est. value', get: (r: FosterRow) => Number(r.est_value), numeric: true, align: 'right', render: (r: FosterRow) => `$${Number(r.est_value).toFixed(2)}` },
          ] as Column<FosterRow>[]}
          rows={rows}
          rowKey={(r) => r.foster_email}
          exportName={`fosters-${period}`}
          empty="No reservations for this period."
        />
      )}
    </>
  );
}

function Summary({ toast }: { toast: (t: string, k?: 'ok' | 'err' | 'info') => void }) {
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [totals, setTotals] = useState<{ total_est_value: string; total_units: number } | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function run() {
    try {
      const res = await callFn<{ rows: SummaryRow[]; totals: { total_est_value: string; total_units: number } }>('report_donations_summary', { from, to });
      setRows(res.rows); setTotals(res.totals); setLoaded(true);
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  return (
    <>
      <div className="toolbar">
        <label className="field">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="field">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button className="btn small" onClick={run} style={{ alignSelf: 'flex-end' }}>Run</button>
      </div>
      {loaded && (
        <>
          {totals && <div className="notice info">Total estimated value: <b>${Number(totals.total_est_value).toFixed(2)}</b> across {totals.total_units} units. <span className="muted">Rows are broken out by tag; an item with multiple tags is listed under each, so per-row subtotals can exceed these totals.</span></div>}
          <DataTable
            columns={[
              { key: 'category', label: 'Category', get: (r: SummaryRow) => r.category },
              { key: 'tag', label: 'Tag', get: (r: SummaryRow) => r.tag },
              { key: 'condition', label: 'Condition', get: (r: SummaryRow) => r.condition, render: (r: SummaryRow) => <span className={`badge ${r.condition}`}>{r.condition}</span> },
              { key: 'line_items', label: 'Line items', get: (r: SummaryRow) => r.line_items, numeric: true, align: 'right' },
              { key: 'units', label: 'Units', get: (r: SummaryRow) => r.total_units, numeric: true, align: 'right' },
              { key: 'value', label: 'Est. value', get: (r: SummaryRow) => Number(r.est_value), numeric: true, align: 'right', render: (r: SummaryRow) => `$${Number(r.est_value).toFixed(2)}` },
            ] as Column<SummaryRow>[]}
            rows={rows}
            rowKey={(r) => `${r.category}|${r.condition}|${r.tag}`}
            exportName={`donations-${from}_${to}`}
            empty="No donations in this range."
          />
        </>
      )}
    </>
  );
}

function ItemsReport({ toast }: { toast: (t: string, k?: 'ok' | 'err' | 'info') => void }) {
  const [rows, setRows] = useState<Item[]>([]);
  const [loaded, setLoaded] = useState(false);

  async function run() {
    try { setRows(await callFn<Item[]>('report_items')); setLoaded(true); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  const cols: Column<Item>[] = [
    { key: 'description', label: 'Item', get: (i) => i.description },
    { key: 'category', label: 'Category', get: (i) => i.category_name || '—' },
    { key: 'condition', label: 'Condition', get: (i) => i.condition, render: (i) => <span className={`badge ${i.condition}`}>{i.condition}</span> },
    { key: 'location', label: 'Location', get: (i) => i.location_name || 'unset' },
    { key: 'avail', label: 'Avail.', get: (i) => i.quantity_available, numeric: true, align: 'right', render: (i) => `${i.quantity_available}/${i.quantity_total}` },
    { key: 'value', label: 'Est. value', get: (i) => (i.estimated_value != null ? Number(i.estimated_value) : -1), numeric: true, align: 'right', render: (i) => (i.estimated_value != null ? `$${Number(i.estimated_value).toFixed(2)}` : '—') },
    { key: 'status', label: 'Status', get: (i) => i.status, render: (i) => <span className={`badge ${i.status === 'available' ? 'new' : 'gray'}`}>{i.status}</span> },
    {
      key: 'tags', label: 'Tags', get: (i) => tagNames(i.tags),
      render: (i) => (i.tags && i.tags.length > 0
        ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{i.tags.map((t) => <span key={t.id} className="tag-chip" style={{ background: t.color || '#dce6f1' }}>{t.name}</span>)}</div>
        : <span className="muted">—</span>),
    },
  ];

  return (
    <>
      <div className="toolbar">
        <button className="btn small" onClick={run}>Run</button>
        <span className="page-sub" style={{ margin: 0, alignSelf: 'center' }}>Every catalog item with its tags — sort, filter, and export.</span>
      </div>
      {loaded && (
        <DataTable columns={cols} rows={rows} rowKey={(i) => i.id} exportName="items" empty="No items." />
      )}
    </>
  );
}
